<template>
  <cards :row="true" class="!items-stretch h-full">
    <Card class="w-1/2 h-full">
      <div class="flex flex-col h-full">
        <div class="relative h-full">
          <Image
            :busy="loading"
            alt="Photo"
            :src="photo?.file?.contentUrl"
            :enlargeable="true"
            :rotation-enabled="true"
            :file-entity="photo?.file"
          />
        </div>
        <div class="text-pre-title-big text-primary mt-5 mb-2">
          {{ $t('pages.images.photos.ratingModal.rateImage') }}
        </div>
        <star-rating
          v-if="internalPhoto"
          :rating="internalPhoto.rating"
          :increment="1"
          :show-rating="false"
          :star-size="15"
          :border-width="5"
          :padding="0"
          :inline="true"
          :read-only="loading"
          inactive-color="var(--color-background-light-100)"
          :border-color="loading ? 'gray' : 'black'"
          :active-border-color="loading ? 'gray' : 'black'"
          :active-color="loading ? 'gray' : 'black'"
          class="star-rating"
          @update:rating="update"
        />
      </div>
    </Card>
    <Card class="w-1/2">
      <Readonly class="w-full mb-3" :label="$t('pages.images.photos.ratingModal.driver')">
        <RouterLink v-if="owner?.id" :to="{ name: 'driver-details', params: { driverId: owner?.id ?? '' } }">
          {{ !!owner?.user ? `${owner.user.firstName} ${owner.user.lastName}` : '' }}
        </RouterLink>
      </Readonly>
      <div v-if="!isAdmin">
        <Readonly class="w-full mb-3" :label="$t('pages.images.photos.ratingModal.campaign')">
          {{ props?.photo?.campaignRegistration?.campaign?.name ?? '' }}
        </Readonly>
        <Readonly class="w-full mb-3" :label="$t('photos.focusPoint')">
          {{ props?.photo?.focusPointLedger?.focusPoint?.name ?? '-' }}
        </Readonly>
      </div>
      <div v-else class="flex gap-x-10 items-center">
        <div class="grow">
          <Readonly
            class="w-full mb-3 custom-readonly"
            :class="{ '!border-red': isEditing }"
            :label="$t('pages.images.photos.ratingModal.campaign')"
          >
            <div class="flex gap-x-4 items-center justify-between pr-4">
              <div v-if="!isEditing">
                {{ props?.photo?.campaignRegistration?.campaign?.name ?? '' }}
              </div>
              <div v-else class="grow pr-5">
                <Multiselect
                  v-model="selectedCampaignRegistration"
                  :options="campaignRegistrations"
                  :loading="loadingCampaigns"
                  class="w-full !rounded !border border-element-neutral"
                  :can-deselect="true"
                  :can-clear="true"
                  :searchable="true"
                />
              </div>
            </div>
          </Readonly>
          <Readonly
            class="w-full mb-3 custom-readonly"
            :class="{ '!border-red': isEditing }"
            :label="$t('photos.focusPoint')"
          >
            <div class="flex justify-between">
              <div>
                {{ deleteFocusPoint ? '-' : props?.photo?.focusPointLedger?.focusPoint?.name ?? '-' }}
              </div>
              <CloseIcon v-if="isEditing && props?.photo?.focusPointLedger" class="cursor-pointer w-5 h-5 mr-5" @click="markDeleteFocusPoint" />
            </div>
          </Readonly>
        </div>
        <div>
          <CustomButton
            v-if="!isEditing"
            variant="primary"
            class="py-2 px-3 !text-input"
            @click="startEditing"
          >
            {{ $t('edit') }}
          </CustomButton>
          <template v-else>
            <CustomButton
              variant="outlined"
              class="py-2 px-3 !text-input"
              :disabled="loading2"
              @click="cancelEditing"
            >
              {{ $t('cancel') }}
            </CustomButton>
            <CustomButton
              variant="primary"
              class="py-2 px-3 !text-input"
              :busy="loading2"
              :disabled="loading2"
              @click="saveEditing"
            >
              {{ $t('save') }}
            </CustomButton>
          </template>
        </div>
      </div>
      <Readonly class="w-full mb-3" :label="$t('pages.images.photos.ratingModal.license')">
        <RouterLink
          v-if="props.photo?.licenseSeatRegistration?.licenseSeat?.license?.company"
          :to="{name: 'company-license-details',
                params: { companyId: props.photo.licenseSeatRegistration.licenseSeat.license.company.id },
                query: {
                  license: JSON.stringify(props.photo.licenseSeatRegistration.licenseSeat.license['@id']),
                }}"
        >
          {{ formatLicenseSeatName(props.photo.licenseSeatRegistration.licenseSeat) }}
        </RouterLink>
        <span v-else>-</span>
      </Readonly>
      <Readonly class="w-full mb-3" :label="$t('region')">
        {{ props?.photo?.campaignRegistration?.campaign?.region?.name ?? props?.photo?.owner?.region?.name ?? '' }}
      </Readonly>
      <Readonly class="w-full mb-3" :label="$t('pages.images.photos.ratingModal.canton')">
        {{
          props?.photo?.owner?.canton
            ? t(`canton.${props?.photo?.owner?.canton}`)
            : ''
        }}
      </Readonly>
      <Readonly class="w-full mb-3" :label="$t('pages.images.photos.ratingModal.coordinates')">
        {{
          props?.photo?.gisLocation
            ? `${props.photo.gisLocation.lat}, ${props.photo.gisLocation.long}`
            : ''
        }}
      </Readonly>
    </Card>
  </cards>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import Cards from '@utils/Cards.vue';
import Card from '@utils/Card.vue';
import Image from '@utils/Image.vue';
import Readonly from '@utils/Readonly.vue';
import StarRating from 'vue-star-rating';
import { useVModel } from '@vueuse/core';
import { useFetchAction } from '@hooks/fetch';
import { useI18n } from 'vue-i18n';
import { useNotify } from '@utils/notifications';
import CustomButton from '@utils/CustomButton.vue';
import Multiselect from '@utils/Multiselect.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import type { Option } from '@utils/filter/filterTypes/Dropdown.vue';
import CloseIcon from '@images/close.svg?component';
import { format } from 'date-fns';
import { useStore } from '@/store';
import type { CampaignRegistration, LicenseSeat, Photo } from '@/entities/Entities';

interface Props {
  photo: Photo;
  loadingRated: boolean;
}

const store = useStore();

const isAdmin = computed(() => store.getters['auth/IS_ADMIN']);

const props = defineProps<Props>();
const emit = defineEmits(['update:photo', 'rated', 'update:loading-rated']);
const internalPhoto = useVModel(props, 'photo', emit);

const owner = computed(() => props?.photo?.owner);

const { t } = useI18n();

const notify = useNotify();

const [exec, { loading }] = useFetchAction();

const isEditing = ref(false);

const save = async (newValue: number) => {
  try {
    await exec(props.photo['@id'], {
      method: 'PUT',
      body: {
        rating: newValue,
      },
    });

    emit('update:loading-rated', false);
    emit('rated');
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.images.photos.ratingModal.saveError'),
    });
    emit('update:loading-rated', false);
  }
};

const update = (newValue: number) => {
  if (isEditing.value === false && internalPhoto.value.rating !== newValue) {
    internalPhoto.value.rating = newValue;
    emit('update:loading-rated', true);
    save(newValue);
  }
};

const campaignRegistrations = ref<Option[]>([]);

const selectedCampaignRegistration = ref();
const deleteFocusPoint = ref(false);

const markDeleteFocusPoint = () => {
  deleteFocusPoint.value = true;
};

const unmarkDeleteFocusPoint = () => {
  deleteFocusPoint.value = false;
};

const loadingCampaigns = ref(false);

const startEditing = async () => {
  if (!props.photo.owner?.id) {
    return;
  }
  isEditing.value = true;
  emit('update:loading-rated', true);
  try {
    loadingCampaigns.value = true;
    campaignRegistrations.value = await collOptionsProvider<CampaignRegistration>(
      '/campaign_registrations',
      {
        filter: {
          driver: props.photo.owner.id,
        },
      },
      (value) => ({
        label: value.campaign.name,
        value: value['@id'],
      }),
    )();
    if (selectedCampaignRegistration.value === undefined) {
      selectedCampaignRegistration.value = props.photo?.campaignRegistration?.['@id'];
    }
    loadingCampaigns.value = false;
  } catch (e) {
    isEditing.value = false;
    emit('update:loading-rated', false);
  }
};

const [exec2, { loading: loading2 }] = useFetchAction();

const saveEditing = async () => {
  try {
    const newPhoto = await exec2(props.photo['@id'], {
      method: 'PUT',
      body: {
        campaignRegistration: selectedCampaignRegistration.value,
        focusPointLedger: deleteFocusPoint.value ? null : undefined,
      },
    });

    emit('update:photo', newPhoto);

    isEditing.value = false;
    campaignRegistrations.value = [];
    selectedCampaignRegistration.value = undefined;
    emit('update:loading-rated', false);
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  }
};

const cancelEditing = async () => {
  isEditing.value = false;
  campaignRegistrations.value = [];
  selectedCampaignRegistration.value = undefined;
  unmarkDeleteFocusPoint();
  emit('update:loading-rated', false);
};

const formatLicenseSeatName = (licenseSeat?: LicenseSeat) => {
  if (!licenseSeat) return '-';
  const license = licenseSeat?.license;
  if (!license) return '-';
  return `${license.company?.name} - ${license.startDate ? format(new Date(license.startDate), 'dd.MM.yyyy') : ''} - ${license.endDate ? format(new Date(license.endDate), 'dd.MM.yyyy') : ''}`;
};
</script>

<style lang="scss" scoped>
:deep(.card-body) {
  height: 100%;
}

:deep(img) {
  position: absolute;
  object-fit: contain;
  height: 100%;
}

.star-rating:deep(.vue-star-rating-star) {
  margin-right: 0 !important;

  svg {
    height: 4rem;
    width: 4rem;
    padding-right: 2rem;
  }
}

:deep(.custom-readonly .field:focus-within) {
  outline: none;
}
</style>
